/**
 * Create Venue page for venue owners.
 * Allows creating a new venue.
 */

import { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../utils/trpc';
import style from '../styles/VenueForm.module.css';
import { useAlertStore } from '../utils/alert';
import TagManagerModal from './TagManagerModal';
import type { LatLng } from '../utils/location';
import { useAuth } from '../contexts/AuthContext';

const GoogleLocationPicker = lazy(() => import('../components/GoogleLocationPicker'));

export default function CreateVenue() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    venueName: '',
    address: '',
    location: null as LatLng | null,
    openTime: '',
    closeTime: '',
    maxCapacity: '',
    tags: [] as { id: number; name: string }[],
  });
  const [validating, setValidating] = useState(false);
  const [isTagSelectorOpen, setIsTagSelectorOpen] = useState(false);
  const { isAuthenticated, isLoading } = useAuth();

  /** Fetch user profile from backend */
  const profileQuery = trpc.venueOwner.getMyProfile.useQuery(undefined, {
    enabled: isAuthenticated,
    retry: false,
  });

  /** Redirect unauthenticated users */
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isLoading, isAuthenticated, navigate]);

  /**
   * Redirect if not a venue owner
   */
  useEffect(() => {
    if (profileQuery.isFetched && !profileQuery.data) {
      navigate('/dashboard');
    }
  }, [profileQuery.isFetched, profileQuery.data, navigate]);

  const createVenue = trpc.venue.create.useMutation({
    onSuccess: (_data) => {
      navigate(`/venue-owner/profile`);
    },
    onError: (_error) => {
      useAlertStore.getState().showAlert({
        header: 'Error',
        body: 'Error creating venue.',
        button: 'Ok',
      });
    },
  });

  const handleFormData = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isValid = () => {
    return (
      formData.venueName.trim() !== '' &&
      formData.address.trim() !== '' &&
      formData.location !== null &&
      formData.openTime !== '' &&
      formData.closeTime !== '' &&
      Number(formData.maxCapacity) > 0
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidating(true);

    if (!isValid()) return;

    createVenue.mutate({
      venue_name: formData.venueName.trim(),
      address: formData.address.trim() || undefined,
      location: formData.location ?? undefined,
      open_time: formData.openTime || undefined,
      close_time: formData.closeTime || undefined,
      max_capacity: formData.maxCapacity ? parseInt(formData.maxCapacity) : undefined,
      tags: formData.tags.map((tag) => tag.name),
    });
  };

  return (
    <div className={style.container}>
      <div className={style.formContainer}>
        <h1>Create New Venue</h1>

        <form onSubmit={handleSubmit} className={style.form}>
          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabel}>
              <label htmlFor="venueName">Venue Name</label>
              <input
                id="venueName"
                type="text"
                value={formData.venueName}
                onChange={(e) => handleFormData('venueName', e.target.value)}
                className={`${style.field} ${validating && !formData.venueName ? style.invalid : ''}`}
                placeholder="e.g. The Soup Kitchen"
              />
            </div>
            {validating && !formData.venueName && (
              <p className={style.reminder}>Venue Name is required.</p>
            )}
          </div>

          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabelColumn}>
              <label htmlFor="address">Address and Map Location</label>
              <Suspense
                fallback={<div className={style.field}>Loading map picker...</div>}
              >
                <GoogleLocationPicker
                  address={formData.address}
                  selectedLocation={formData.location}
                  onAddressChange={(value) => handleFormData('address', value)}
                  onLocationChange={(location) =>
                    setFormData((prev) => ({ ...prev, location }))
                  }
                  invalid={validating && (!formData.address || !formData.location)}
                />
              </Suspense>
            </div>
            {validating && (!formData.address || !formData.location) && (
              <p className={style.reminder}>
                Address and map-selected location are required.
              </p>
            )}
          </div>

          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabel}>
              <label htmlFor="openTime">Opens At</label>
              <input
                id="openTime"
                type="time"
                value={formData.openTime}
                onChange={(e) => handleFormData('openTime', e.target.value)}
                className={`${style.field} ${validating && !formData.openTime ? style.invalid : ''}`}
              />
            </div>
          </div>

          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabel}>
              <label htmlFor="closeTime">Closes At</label>
              <input
                id="closeTime"
                type="time"
                value={formData.closeTime}
                onChange={(e) => handleFormData('closeTime', e.target.value)}
                className={`${style.field} ${validating && !formData.closeTime ? style.invalid : ''}`}
              />
            </div>
          </div>

          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabel}>
              <label htmlFor="maxCapacity">Capacity</label>
              <input
                id="maxCapacity"
                type="number"
                min="1"
                value={formData.maxCapacity}
                onChange={(e) => handleFormData('maxCapacity', e.target.value)}
                className={`${style.field} ${validating && Number(formData.maxCapacity) <= 0 ? style.invalid : ''}`}
              />
            </div>
            {validating && Number(formData.maxCapacity) <= 0 && (
              <p className={style.reminder}>Capacity must be greater than 0.</p>
            )}
          </div>

          <div className={style.fieldContainer}>
            <div className={style.fieldWithLabel}>
              <label>Interests</label>
              <button
                type="button"
                onClick={() => setIsTagSelectorOpen(true)}
                className={style.button}
              >
                Select Interests
              </button>
            </div>
          </div>

          <div className={style.buttonContainer}>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className={`${style.button} ${style.cancelButton}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createVenue.isPending}
              className={`${style.button} ${style.submitButton}`}
            >
              {createVenue.isPending ? 'Creating...' : 'Create Venue'}
            </button>
          </div>
        </form>
      </div>
      <TagManagerModal
        isOpen={isTagSelectorOpen}
        onClose={() => setIsTagSelectorOpen(false)}
        mode="select"
        min={1}
        max={10}
        onSave={(selectedTags) => {
          setFormData((prev) => ({ ...prev, tags: selectedTags }));
          setIsTagSelectorOpen(false);
        }}
        initialTags={formData.tags}
      />
    </div>
  );
}
